import { CopyButton, Button } from '@mantine/core';
import { IconCopy } from '@tabler/icons-react';
import { OnChangeFn } from '@tanstack/react-table';
import {
  MRT_ColumnDef,
  MRT_RowSelectionState,
  MRT_TableOptions,
} from 'mantine-react-table';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { create } from 'zustand';

import { SampleStatus } from '../../data-access/types/sampling';
import { useOrderPage } from '../../hooks/use-order-page';
import { SampleStatusPill } from '../sample-status';

import { formatDate } from '@data-access';
import { PfTable } from '@ui';

interface RowSelectionStore {
  rowSelection: MRT_RowSelectionState;
  setRowSelection: OnChangeFn<MRT_RowSelectionState>;
}
export const useOrderPointsSelectionStore = create<RowSelectionStore>(
  (set) => ({
    rowSelection: {},
    setRowSelection: (rowSelection) =>
      set((state) => ({
        rowSelection:
          typeof rowSelection === 'function'
            ? rowSelection(state.rowSelection)
            : rowSelection,
      })),
  }),
);

const OrderPointsTable: React.FC = () => {
  const { t } = useTranslation();
  const { order } = useOrderPage();
  const rowSelection = useOrderPointsSelectionStore(
    (state) => state.rowSelection,
  );
  const setRowSelection = useOrderPointsSelectionStore(
    (state) => state.setRowSelection,
  );

  const rows = order.data?.samples.map((sample) => ({
    id: sample.id,
    code: sample.code,
    status: '',
    field_id: null,
    field_name: '',
    sampled_at: '',
    conditions: '',
    comment: '',
    zone: 0,
    analysis_type: '',
    subRows: sample.points.features.map((point) => point.properties),
  }));

  type RowData = (typeof rows)[number] &
    (typeof rows)[number]['subRows'][number];

  const columns = useMemo<MRT_ColumnDef<RowData>[]>(
    () => [
      {
        accessorKey: 'code',
        header: t('common.code'),
        Cell: ({ cell }) => (
          <CopyButton value={cell.getValue<string>()}>
            {({ copied, copy }) => (
              <Button
                color={copied ? 'pf-green.7' : 'dark'}
                onClick={copy}
                rightSection={copied ? '✓' : <IconCopy size={12} />}
                size="compact-xs"
                variant="subtle"
              >
                {cell.getValue<string>()}
              </Button>
            )}
          </CopyButton>
        ),
      },
      {
        accessorKey: 'status',
        header: t('table.columns.status'),
        Cell: ({ cell }) => {
          const status = cell.getValue<SampleStatus>();
          if (!status) return '-';

          return <SampleStatusPill size="md" status={status} />;
        },
      },
      {
        accessorKey: 'field_name',
        header: t('common.parcelle'),
        Cell: ({ cell }) => cell.getValue<string>() || '-',
      },
      {
        accessorKey: 'sampled_at',
        header: t('common.date-de-prelevement'),
        Cell(props) {
          const date = props.cell.getValue<string>();
          return (
            formatDate({
              date: date,
              format: 'display-hour',
            }) ?? '-'
          );
        },
      },
      {
        accessorKey: 'conditions',
        header: t('common.conditions-de-prelevement'),
        size: 200,
        Cell: ({ cell }) => cell.getValue<string>() || '-',
      },
      {
        accessorKey: 'comment',
        header: t('cmoon.commentaire'),
        Cell: ({ cell }) => cell.getValue<string>() || '-',
      },
    ],
    [t],
  );

  const tableOptions: MRT_TableOptions<RowData> = {
    columns,
    data: rows,
    enableExpanding: true,
    enableRowSelection: (row) => row.original.subRows?.length > 0,
    onRowSelectionChange: setRowSelection,
    state: {
      rowSelection,
      columnOrder: [
        'mrt-row-select',
        'mrt-row-expand',
        'code',
        'status',
        'field_name',
        'sampled_at',
        'conditions',
        'comment',
      ],
    },
    getRowId: (row) => row.id,
    initialState: {
      expanded: true,
    },
    mantineSelectCheckboxProps: ({ row }) => ({
      checked: row.getIsSelected(),
      onClick: row.getToggleSelectedHandler(),
    }),
  };

  return <PfTable<RowData> options={tableOptions} />;
};

export default OrderPointsTable;
